import {
  Box,
  Button,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Card } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import { useInvoiceStore } from "@src/components/modules/invoices/Create/store";
import {
  type BodyColumnDef,
  DataTable,
  LoadingOverlay,
} from "@src/components/ui-kit";
import { currency } from "@src/utils/formatters";
import { formatSecondsToHours } from "@src/utils/time";
import { observer } from "mobx-react-lite";
import { Fragment, useMemo } from "react";
import type { TimeAndMaterialsBudgetItemForm } from "./forms";
import type { TimeAndMaterialsTableState } from "./table-state";

type TimeAndMaterialsTableRow =
  | {
      id: string;
      rowType: "parent";
      item: TimeAndMaterialsBudgetItemForm;
      __subRows?: TimeAndMaterialsTableRow[];
    }
  | {
      id: string;
      rowType: "subrow";
      item: TimeAndMaterialsBudgetItemForm["__subRows"][0];
      parent: TimeAndMaterialsBudgetItemForm;
    };

const isParentRow = (
  row: TimeAndMaterialsTableRow,
): row is Extract<TimeAndMaterialsTableRow, { rowType: "parent" }> =>
  row.rowType === "parent";

const CommissionCell = observer(function CommissionCell({
  item,
  selectedIds,
}: {
  item: TimeAndMaterialsBudgetItemForm;
  selectedIds: string[] | undefined;
}) {
  return (
    !!item.commission && (
      <Tooltip label={t`Commission`} placement="top">
        <HStack>
          <InputGroup
            visibility={
              item.isRowOrChildSelected(selectedIds) ? "visible" : "hidden"
            }
            size="xs"
          >
            <Input
              onChange={(event) => {
                item.handleCommissionInputChange(event, selectedIds);
              }}
              type="number"
              value={item.commissionInputValue}
            />
            <InputRightElement pr="4" fontSize="xx-small">
              <Trans>%</Trans>
            </InputRightElement>
          </InputGroup>
        </HStack>
      </Tooltip>
    )
  );
});

const AmountToInvoiceCell = observer(function AmountToInvoiceCell({
  item,
  currencyCode,
  selectedIds,
}: {
  item: TimeAndMaterialsBudgetItemForm;
  currencyCode: string | undefined;
  selectedIds: string[] | undefined;
}) {
  return (
    <Tooltip
      isDisabled={!item.isRowOrChildSelected(selectedIds)}
      label={t`Amount to invoice`}
      placement="top"
    >
      <HStack>
        <InputGroup
          visibility={
            item.isRowOrChildSelected(selectedIds) ? "visible" : "hidden"
          }
          size="xs"
        >
          <Input
            onChange={(event) => {
              item.handleAmountInputChange(event);
            }}
            type="number"
            value={item.amountInputValue}
          />
          <InputRightElement pr="4" fontSize="xx-small">
            {currencyCode}
          </InputRightElement>
        </InputGroup>
      </HStack>
    </Tooltip>
  );
});

const OriginalValueCell = observer(function OriginalValueCell({
  item,
  selectedIds,
}: {
  item: TimeAndMaterialsBudgetItemForm;
  selectedIds: string[] | undefined;
}) {
  const selectedAmount = item.addPercent(
    item.getSelectedAmount(selectedIds ?? []),
    item.commission?.$ ?? 0,
  );

  if (
    !item.amountToInvoice.dirty ||
    item.amountToInvoice.$ === selectedAmount
  ) {
    return null;
  }

  return (
    <Button
      onClick={() => {
        item.setAmountToInvoice(selectedAmount);
      }}
      size="xs"
      type="button"
      variant="link"
    >
      ({currency.formatByWorkspace(selectedAmount)})
    </Button>
  );
});

const TotalToInvoiceHeader = observer(function TotalToInvoiceHeader({
  state,
}: {
  state: TimeAndMaterialsTableState;
}) {
  return (
    <Fragment>{currency.formatByWorkspace(state.totalToInvoice)}</Fragment>
  );
});

function getColumns(
  state: TimeAndMaterialsTableState,
  tableTitle: string,
): BodyColumnDef<TimeAndMaterialsTableRow>[] {
  return [
    {
      id: "title",
      meta: { label: tableTitle },
      header: () => tableTitle,
      cell: ({ row }) =>
        isParentRow(row.original) ? (
          <Text>{row.original.item.title}</Text>
        ) : (
          <Text>
            {row.original.item.name}&nbsp;(
            {row.original.item.tracked_time && (
              <Fragment>
                {formatSecondsToHours(row.original.item.tracked_time)},&nbsp;
              </Fragment>
            )}
            {currency.formatByWorkspace(row.original.item.amount ?? 0)})
          </Text>
        ),
    },
    {
      id: "commission",
      meta: { align: "left", label: "" },
      header: () => "",
      cell: ({ row }) =>
        isParentRow(row.original) ? (
          <CommissionCell
            item={row.original.item}
            selectedIds={state.selectedIds}
          />
        ) : null,
    },
    {
      id: "uninvoiced",
      meta: { padding: 0, label: "" },
      header: () => null,
      cell: ({ row }) =>
        isParentRow(row.original) ? (
          <AmountToInvoiceCell
            item={row.original.item}
            currencyCode={state.currencyCode}
            selectedIds={state.selectedIds}
          />
        ) : null,
    },
    {
      id: "original_value",
      meta: { align: "left", label: "" },
      header: () => "",
      cell: ({ row }) =>
        isParentRow(row.original) ? (
          <OriginalValueCell
            item={row.original.item}
            selectedIds={state.selectedIds}
          />
        ) : null,
    },
    {
      id: "total",
      meta: { align: "right", padding: 0, label: t`Total` },
      header: () => <TotalToInvoiceHeader state={state} />,
    },
  ];
}

type TableProps = {
  tableTitle: string;
  state: TimeAndMaterialsTableState;
  isFetching: boolean;
};

const Table = observer(function Table({
  isFetching,
  state,
  tableTitle,
}: TableProps) {
  const columns = useMemo(
    () => getColumns(state, tableTitle),
    [state, tableTitle],
  );
  const data = useMemo<TimeAndMaterialsTableRow[]>(
    () =>
      state.tableData.map((item) => ({
        id: item.id,
        rowType: "parent",
        item,
        __subRows: item.__subRows.map((subRow) => ({
          id: subRow.id,
          rowType: "subrow",
          item: subRow,
          parent: item,
        })),
      })),
    [state.tableData],
  );

  return (
    <Card maxW="full" minW="0" overflow="hidden" style={{ contain: "content" }}>
      <Box pos="sticky" top="0" minW="0" maxW="full">
        <LoadingOverlay isLoading={isFetching} size="lg">
          <Box overflowX="auto" minW="0" maxW="full">
            <DataTable>
              <DataTable.Body
                tableContainerSx={{
                  roundedTop: "lg",
                  maxW: "full",
                  overflowX: "auto",
                }}
                columns={columns}
                data={data}
                tableKey={state.tableKey}
                allIds={state.allIds}
                selected={state.selectedIds}
                onSelect={state.handleSelectRows}
                enableColumnVisibilityControl={false}
                disableBulkActions
                enableRowSelection
                enableSubRowSelection
                rowSelectionMode="subrowsOrParent"
                initialExpanded
              />
            </DataTable>
          </Box>
        </LoadingOverlay>
      </Box>
    </Card>
  );
});

export const TimeAndMaterialsProjectCard = observer(
  function TimeAndMaterialsProjectCard() {
    const { timeAndMaterialsStore: store } = useInvoiceStore();

    return (
      <Fragment>
        <Table
          state={store.workTableState}
          tableTitle={`${store.projectTitle} - ${t`Work`}`}
          isFetching={store.fetchingData}
        />
        {store.expenseTableState.tableData.length > 0 && (
          <Table
            state={store.expenseTableState}
            tableTitle={`${store.projectTitle} - ${t`Expense`}`}
            isFetching={store.fetchingData}
          />
        )}
      </Fragment>
    );
  },
);
